import { supabase } from './supabase'

// MinerU API 配置
const MINERU_API_URL = 'https://mineru.net/api/v4/extract/task'
const MINERU_STATUS_URL = 'https://mineru.net/api/v4/extract/task'

export type OcrProvider = 'baidu' | 'mineru'

export interface OcrResult {
  text: string
  confidence: number
  pages: Array<{ pageNumber: number; text: string; confidence: number }>
}

/**
 * 上传文件到 Supabase Storage
 */
export async function uploadFileToStorage(file: File, caseId: string): Promise<string> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'pdf'
  const fileName = `${caseId}/${Date.now()}.${fileExt}`

  const { data, error } = await supabase.storage
    .from('documents')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    })

  if (error) throw error

  const { data: { publicUrl } } = supabase.storage
    .from('documents')
    .getPublicUrl(fileName)

  return publicUrl
}

/**
 * 调用 MinerU API 进行 PDF 识别
 * MinerU 需要文件 URL，所以先上传到 Storage 再调用
 */
export async function callMinerUOCR(fileUrl: string, options?: {
  modelVersion?: 'pipeline' | 'vlm' | 'MinerU-HTML'
}): Promise<OcrResult> {
  const apiKey = import.meta.env.VITE_MINERU_API_TOKEN
  if (!apiKey) {
    throw new Error('MinerU API Token 未配置，请在系统设置中配置')
  }

  const modelVersion = options?.modelVersion || 'pipeline'

  // 1. 提交提取任务
  const submitResponse = await fetch(MINERU_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      url: fileUrl,
      model_version: modelVersion,
    }),
  })

  const submitData = await submitResponse.json()
  if (submitData.code !== 0) {
    throw new Error(`提交任务失败: ${submitData.msg}`)
  }

  const taskId = submitData.data?.task_id
  if (!taskId) {
    throw new Error('未能获取任务 ID')
  }

  // 2. 轮询任务状态
  const maxRetries = 60 // 最多等待 5 分钟 (60 * 5s)
  for (let i = 0; i < maxRetries; i++) {
    await new Promise(resolve => setTimeout(resolve, 5000)) // 每 5 秒检查一次

    const statusResponse = await fetch(`${MINERU_API_URL}/${taskId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    })

    const statusData = await statusResponse.json()
    if (statusData.code !== 0) {
      throw new Error(`查询任务状态失败: ${statusData.msg}`)
    }

    const state = statusData.data?.state
    const fullZipUrl = statusData.data?.full_zip_url

    if (state === 'done' && fullZipUrl) {
      // 3. 下载并解析结果
      return await parseMinerUResult(fullZipUrl)
    } else if (state === 'failed') {
      throw new Error('MinerU 任务处理失败')
    }
  }

  throw new Error('MinerU 任务超时，请稍后重试')
}

/**
 * 解析 MinerU 返回的 ZIP 包
 * MinerU 返回的是 ZIP 包，包含 markdown、图片等文件
 */
async function parseMinerUResult(zipUrl: string): Promise<OcrResult> {
  const apiKey = import.meta.env.VITE_MINERU_API_TOKEN
  
  // 下载 ZIP 文件
  const zipResponse = await fetch(zipUrl)
  if (!zipResponse.ok) {
    throw new Error('下载 MinerU 结果失败')
  }

  const zipBuffer = await zipResponse.arrayBuffer()
  const zipBlob = new Blob([zipBuffer])

  // 使用 JSZip 解析 ZIP
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(zipBlob)

  // 查找 markdown 文件
  let markdownContent = ''
  const files = Object.keys(zip.files)
  
  for (const fileName of files) {
    if (fileName.endsWith('.md')) {
      const file = zip.file(fileName)
      if (file) {
        markdownContent = await file.async('text')
        break
      }
    }
  }

  // 如果没有找到 markdown，尝试其他文本文件
  if (!markdownContent) {
    for (const fileName of files) {
      if (fileName.endsWith('.txt')) {
        const file = zip.file(fileName)
        if (file) {
          markdownContent = await file.async('text')
          break
        }
      }
    }
  }

  // 解析结果
  const lines = markdownContent.split('\n').filter(line => line.trim())
  const fullText = lines.join('\n')

  return {
    text: fullText,
    confidence: 0.95, // MinerU 默认高置信度
    pages: [
      {
        pageNumber: 1,
        text: fullText,
        confidence: 0.95,
      },
    ],
  }
}

/**
 * 调用百度 OCR
 */
export async function callBaiduOCR(file: File): Promise<OcrResult> {
  const API_KEY = import.meta.env.VITE_BAIDU_OCR_API_KEY
  const SECRET_KEY = import.meta.env.VITE_BAIDU_OCR_SECRET_KEY

  if (!API_KEY || !SECRET_KEY) {
    throw new Error('百度 OCR API 未配置')
  }

  // 获取 access_token
  const tokenResponse = await fetch(
    `https://aip.baidubce.com/oauth/2.0/token?grant_type=client_credentials&client_id=${API_KEY}&client_secret=${SECRET_KEY}`
  )
  const tokenData = await tokenResponse.json()
  const accessToken = tokenData.access_token

  if (!accessToken) {
    throw new Error('Failed to obtain OCR access token')
  }

  // 读取文件
  const arrayBuffer = await file.arrayBuffer()

  // 调用通用文字识别（高精度）
  const ocrResponse = await fetch(
    `https://aip.baidubce.com/rest/2.0/ocr/v1/general_basic?access_token=${accessToken}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ image: btoa(String.fromCharCode(...new Uint8Array(arrayBuffer))) }),
    }
  )

  const ocrData = await ocrResponse.json()

  if (ocrData.error_code) {
    throw new Error(`OCR error: ${ocrData.error_msg}`)
  }

  // 解析结果
  let fullText = ''
  const pages: Array<{ pageNumber: number; text: string; confidence: number }> = []

  for (const line of ocrData.words_result || []) {
    fullText += line.words + '\n'
  }

  return {
    text: fullText.trim(),
    confidence: ocrData.general_basic?.confidence || 0,
    pages: [{ pageNumber: 1, text: fullText.trim(), confidence: ocrData.general_basic?.confidence || 0 }],
  }
}

/**
 * 统一 OCR 入口函数
 */
export async function performOCR(
  file: File,
  provider: OcrProvider,
  options?: { fileUrl?: string; modelVersion?: 'pipeline' | 'vlm' | 'MinerU-HTML' }
): Promise<OcrResult> {
  if (provider === 'mineru') {
    // MinerU 需要 URL，先上传到 Storage
    if (!options?.fileUrl) {
      throw new Error('MinerU 需要提供文件 URL')
    }
    return callMinerUOCR(options.fileUrl, options)
  } else {
    // 百度 OCR
    return callBaiduOCR(file)
  }
}

/**
 * 从文书内容中自动提取关键信息
 */
export function extractDocInfo(content: string, docType: string) {
  const info: Record<string, string> = {}

  // 提取日期
  const dateMatch = content.match(/(\d{4}[年\-]\d{1,2}[月\-]\d{1,2}日?)/)
  if (dateMatch) info.docDate = dateMatch[1].replace(/[年月]/g, '-').replace('日', '')

  // 提取当事人
  const partyMatch = content.match(/(?:当事人|原告|被告|上诉人|被上诉人)[：:](.+?)(?:，|$|\n)/)
  if (partyMatch) info.party = partyMatch[1].trim()

  // 提取金额
  const amountMatch = content.match(/(?:标的|金额|赔偿|判决)[：:]*\s*([¥￥]?\d+(?:,\d{3})*(?:\.\d+)?)/)
  if (amountMatch) info.amount = amountMatch[1]

  // 提取法院
  const courtMatch = content.match(/(?:人民法院|法院)[：:]?\s*(.+?)(?:的|于|，|$|\n)/)
  if (courtMatch) info.court = courtMatch[1].trim()

  return info
}
